import React, { useState, useEffect, useMemo } from 'react';
import { Users, Pencil, Trash2, Search, BarChart3, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom'; // ⬅ Add this at the top
import PopupModel from '../../../components/popup-model/PopupModel';

import './employee.css';
import {
  getActiveEmployees,
  updateEmployee,
  changeUserStatus,
  getAssignedCandidates,
} from '../../../services/admin/api';

const ActiveEmployee = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignedCandidates, setAssignedCandidates] = useState([]);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('userid');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [employeeToDeactivate, setEmployeeToDeactivate] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isAsc, setIsAsc] = useState(false);
  const [modalInfo, setModalInfo] = useState({
    show: false,
    title: '',
    message: '',
    isSuccess: true,
  });


  const [editFormData, setEditFormData] = useState({
    id: '',
    name: '',
    email: '',
    contact: '',
  });

  const [currentPage, setCurrentPage] = useState(1);

  const fetchEmployees = async (page = 0, size = itemsPerPage, column = sortColumn, sort = isAsc) => {
    try {
      setLoading(true);
      const res = await getActiveEmployees(page, size , sortColumn, sort, searchTerm ); 
      if(res.data?.content){
        setEmployees(res.data.content || []);
        setTotalPages(res.data.totalPages || 0);
        setTotalElements(res.data.totalElements || 0);
        setCurrentPage((res.data.number || 0) + 1);
      }

    } catch (error) {
      setEmployees([]);
      setTotalPages(1)
      setCurrentPage(1);
      setFeedbackMessage(' Failed to load employees.');
      triggerFeedback();
    } finally {
      setLoading(false);
    }
  };


  const fetchAssignedCandidates = async (employeeId) => {
    try {
      const res = await getAssignedCandidates(employeeId);
      setAssignedCandidates(res.data);
    } catch (error) {
      setFeedbackMessage(' Failed to load assigned candidates.');
      triggerFeedback();
    }
  };

  useEffect(() => {
    fetchEmployees(currentPage - 1, itemsPerPage, sortColumn, isAsc); 
  }, [currentPage, itemsPerPage, sortColumn , isAsc, searchTerm]);

  const handleEdit = (employee) => {
    setEditFormData({
      id: employee.id,
      name: employee.name,
      email: employee.email,
      contact: employee.contact,
    });
    setShowEditModal(true);
  };



  const renderSortArrow = (field) => {
    const isActive = sortColumn === field;

    return (
      <span
        className={`sort-icons ${isActive ? "sort-active" : ""
          }`}
      >
        {/* showing the next action button  */}
        {!isActive
          ? "▲▼"
          : sortOrder === "asc"
            ?  "▲" : "▼" 
        }
      </span>
    );
  };


  // handle the sort with consistency icons and defualt sort asc . 
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Same column -> toggle
      const newIsAsc = !isAsc;
      setIsAsc(newIsAsc);
      setSortOrder(newIsAsc ? "asc" : "desc");
    } else {
      // Different column -> default to ascending
      setSortColumn(column);
      setIsAsc(true);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };


  const handleDeleteClick = (employee) => {
    setEmployeeToDeactivate(employee);
    setShowConfirmModal(true);
  };

  const confirmDeactivation = async () => {
    try {
      const response = await changeUserStatus(employeeToDeactivate.id, false);

      await fetchEmployees(
        currentPage - 1,
        itemsPerPage,
        sortColumn,
        isAsc
      );

      if (employees.length === 1 && currentPage > 1) {
        setCurrentPage(prev => prev - 1);
      }

      setFeedbackMessage(
        response?.data?.message || 'Agent Inactivated Successfully'
      );

    } catch (error) {
      setFeedbackMessage(
        error?.response?.data?.message ||
        'Failed to Inactivate the Agent'
      );
    } finally {
      setEmployeeToDeactivate(null);
      setShowConfirmModal(false);
      triggerFeedback();
    }
  };

  const triggerFeedback = () => {
    setShowFeedbackModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();

    const nameRegex = /^[a-zA-Z\s]{1,40}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const contactRegex = /^\d{10}$/;

    if (!nameRegex.test(editFormData.name)) {
      setFeedbackMessage(' Invalid name format.');
      return triggerFeedback();
    }

    if (!emailRegex.test(editFormData.email) || editFormData.email.length > 40) {
      setFeedbackMessage(' Invalid email address.');
      return triggerFeedback();
    }

    if (!contactRegex.test(editFormData.contact)) {
      setFeedbackMessage(' Contact number must be exactly 10 digits.');
      return triggerFeedback();
    }

    const payload = {
      username: editFormData.name,
      email: editFormData.email,
      phonenumber: editFormData.contact,
      role: 'EMPLOYEE',
    };

    try {
      const res = await updateEmployee(editFormData.id, payload);
      setFeedbackMessage((res?.data?.message || 'Employee updated successfully.'));
      setShowEditModal(false);
      await fetchEmployees(
        currentPage - 1,
        itemsPerPage,
        sortColumn,
        isAsc
      );
    } catch (error) {
      const backendMessage = error?.response?.data?.message || error?.message || 'Failed to update employee.';
      setFeedbackMessage(backendMessage);
    } finally {
      triggerFeedback();
    }
  };

  // const filteredEmployees = useMemo(() => {
  //   return [...employees]
  //     .filter((emp) =>
  //       emp.id?.toString().includes(searchTerm) ||
  //       emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       emp.contact?.toString().includes(searchTerm)
  //     )
      // .sort((a, b) => {
      //   if (!sortColumn) return 0;

    //   const valA = a[sortColumn];
    //   const valB = b[sortColumn];

    //   if (valA == null) return 1;
    //   if (valB == null) return -1;

    //   if (typeof valA === 'string' && typeof valB === 'string') {
    //     return sortOrder === 'asc'
    //       ? valA.toLowerCase().localeCompare(valB.toLowerCase())
    //       : valB.toLowerCase().localeCompare(valA.toLowerCase());
    //   }

    //   return sortOrder === 'asc'
    //     ? valA - valB
    //     : valB - valA;
    // });

  // }, [employees, searchTerm, sortColumn, sortOrder]);



  // const paginatedEmployees = useMemo(() => {
  //   const start = (currentPage - 1) * itemsPerPage;
  //   return filteredEmployees.slice(start, start + itemsPerPage);
  // }, [filteredEmployees, currentPage]);

  // const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);

return (
  <div className="admin-active-employee-container">

    {/* Back Button */}
    <div className="active-employee-back-wrapper">
      <button
        type="button"
        className="active-employee-back-button"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>
    </div>

    <div className="admin-employee-table-wrapper">
        <div className="employee-table-controls">

          <div className="entries-select">
            Show
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </div>

          <div className="employee-search-input-wrapper">
            <input
              type="text"
              placeholder="Search employee by name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Search className="employee-search-icon" size={18} />
          </div>

        </div>


        <table className="admin-employee-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('userid')}>
                Agent ID  {renderSortArrow('userid')}
                {/* {sortColumn === 'userid' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
              </th>

              <th onClick={() => handleSort('username')}>
                Name  {renderSortArrow('username')}
                {/* {sortColumn === 'username' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
              </th>

              <th onClick={() => handleSort('email')}>
                Email  {renderSortArrow('email')}
                {/* {sortColumn === 'email' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
              </th>

              <th onClick={() => handleSort('phonenumber')}>
                Contact  {renderSortArrow('phonenumber')}
                {/* {sortColumn === 'phonenumber' ? (sortOrder === 'asc' ? '↑' : '↓') : '↑↓'} */}
              </th>

              <th>Assigned Candidates</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="no-data loading-text">
                  Loading Employees...
                </td>
              </tr>
            ) : employees.length > 0 ? (
                employees.map((employee) => (
                <tr key={employee.id}>
                  <td>{employee.id}</td>
                  <td>{employee.name}</td>
                  <td>{employee.email}</td>
                  <td>{employee.contact}</td>
                  <td>
                    <button
                      className="admin-candidate-list-btn"
                      title="View Assigned Candidates"
                      onClick={() => navigate(`/admin/employee/${employee.id}/candidates`, {
                        state: { employeeName: employee.name }
                      })}
                    >
                      <Users size={18} />
                    </button>
                  </td>
                  <td>
                    <div className="admin-action-btn-group">
                      <button
                        title="Performance Report"
                        onClick={() => navigate(`/admin/employee-performance/${employee.id}`)}
                        className="admin-action-btn"
                      >
                        <BarChart3 size={16} />
                      </button>
                      <button
                        title="Edit"
                        onClick={() => handleEdit(employee)}
                        className="admin-action-btn"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        title="Delete"
                        onClick={() => handleDeleteClick(employee)}
                        className="admin-action-btn delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="no-data">
                  No employees found.
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      <div className="pagination">

        <div className="pagination-summary">
          Showing {(currentPage - 1) * itemsPerPage + 1}
          {' '}to{' '}
          {Math.min(currentPage * itemsPerPage, totalElements)} {' '}
          of {totalElements} entries
        </div>

        <div className="pagination-buttons-user">
          {/* Previous */}
          <button
            className="page-btn"
            onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          {/* Page Numbers */}
          {Array.from({ length: totalPages }, (_, index) => {
            const pageNumber = index + 1;
            return (
              <button
                key={pageNumber}
                className={`page-btn ${currentPage === pageNumber ? "active" : ""}`}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            );
          })}

          {/* Next */}
          <button
            className="page-btn"
            onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            Next
          </button>
        </div>
      </div>



      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <div className="admin-modal-header">
              <h3>Edit Employee</h3>
              <button className="admin-close-icon" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleEditFormSubmit} className="admin-edit-form">
              <div className="admin-form-row">
                <label>ID</label>
                <input type="text" value={editFormData.id} name="id" disabled className="admin-disabled-input" />
              </div>
              <div className="admin-form-row">
                <label>Name</label>
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              <div className="admin-form-row">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={editFormData.email}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              <div className="admin-form-row">
                <label>Contact</label>
                <input
                  type="text"
                  name="contact"
                  value={editFormData.contact}
                  onChange={handleEditFormChange}
                  required
                />
              </div>
              <div className="admin-modal-buttons">
                <button type="button" onClick={() => setShowEditModal(false)} className="admin-assign-cancel">
                  Cancel
                </button>
                <button type="submit" className="admin-assign-confirm">Save</button>

              </div>
            </form>
          </div>
        </div>
      )}

      {showConfirmModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <h3>Confirm Deactivation</h3>
            <p>Are you sure you want to deactivate {employeeToDeactivate?.name}?</p>
            <div className="admin-modal-buttons">
              <button onClick={() => setShowConfirmModal(false)} className="admin-assign-cancel">Cancel</button>
              <button onClick={confirmDeactivation} className="admin-assign-confirm">Yes</button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content">
            <p style={{ textAlign: 'center', fontSize: '16px' }}>{feedbackMessage}</p>
            <div className="admin-popup-modal-buttons">
              <button onClick={() => setShowFeedbackModal(false)} className="admin-popup-ok-btn">
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveEmployee;
